
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FeedbackCategoryTag } from "@/types/feedback";
import { toast } from "sonner";

// Import the components
import ReplyHeader from "./form/ReplyHeader";
import MessageInput from "./form/MessageInput";
import FileUploadButton from "./form/FileUploadButton";
import CategorySelect from "./form/CategorySelect";
import AttachmentPreview from "./form/AttachmentPreview";

interface FeedbackFormProps {
  onSubmit: (message: string, category: FeedbackCategoryTag, attachment?: string | null) => void;
  replyToId?: string | null;
  onCancelReply?: () => void;
  isSubmitting?: boolean;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  onSubmit,
  replyToId,
  onCancelReply = () => {},
  isSubmitting = false,
}) => {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<FeedbackCategoryTag>(null);
  const [attachment, setAttachment] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleFileChange = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setImagePreview(result);
      setAttachment(result);
      setFileType(file.type);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setAttachment(null);
    setFileType(null);
    setFileName(null);
  };

  const handleSubmit = () => {
    if (!message.trim()) {
      toast.error("Bericht mag niet leeg zijn");
      return;
    }

    if (!user) {
      toast.error("Je moet ingelogd zijn om feedback te geven");
      return;
    }

    onSubmit(message, category, attachment);
    setMessage("");
    setCategory(null);
    setAttachment(null);
    setFileType(null);
    setFileName(null);
    setImagePreview(null);
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-4">
        <ReplyHeader replyToId={replyToId} onCancelReply={onCancelReply} />
        <MessageInput message={message} setMessage={setMessage} />
        <AttachmentPreview 
          imagePreview={imagePreview} 
          fileType={fileType}
          fileName={fileName}
          onRemove={handleRemoveImage} 
        />
      </CardContent>
      <CardFooter className="flex justify-between border-t px-4 py-2">
        <div className="flex gap-2 items-center">
          <FileUploadButton 
            onFileChange={handleFileChange} 
            disabled={isSubmitting} 
          />
          <CategorySelect 
            category={category} 
            setCategory={setCategory} 
            disabled={isSubmitting}
          />
        </div>
        <Button onClick={handleSubmit} disabled={isSubmitting || !message.trim()}>
          <Send className="h-4 w-4 mr-1" />
          Versturen
        </Button>
      </CardFooter>
    </Card>
  );
};

export default FeedbackForm;
